import { useContext } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuthContext } from '@cognite/e2e-auth';

import { QueryKeys } from '../../../../../utils/queryKeys';
import type { AppConfig } from '../../../types';

import { UseUpsertAppConfigMutationContext } from './use-upsert-app-config-mutation.context';

export const useUpsertAppConfigMutation = () => {
  const coreAuth = useAuthContext();
  const queryClient = useQueryClient();
  const { getAppConfigClient, captureException } = useContext(
    UseUpsertAppConfigMutationContext
  );

  return useMutation<
    void,
    Error,
    { newAppConfig: Partial<AppConfig> & { externalId: string } }
  >({
    mutationFn: async ({ newAppConfig }) => {
      const schemaVersionOverride =
        coreAuth.authState?.status === 'AUTHENTICATED'
          ? coreAuth.authState.projectInfo?.configurationSchemaVersion
          : undefined;

      const appConfigClient = getAppConfigClient(
        coreAuth.client,
        schemaVersionOverride
      );

      const nextConfig: Partial<AppConfig> & { externalId: string } = {
        externalId: newAppConfig.externalId,
        customerDataSpaceId: newAppConfig.customerDataSpaceId,
        customerDataSpaceVersion: newAppConfig.customerDataSpaceVersion,
        appDataSpaceId: newAppConfig.appDataSpaceId,
        appDataSpaceVersion: newAppConfig.appDataSpaceVersion,
        featureConfiguration: newAppConfig.featureConfiguration,
      };

      await appConfigClient.upsertConfiguration(nextConfig);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.APP_CONFIG],
      });
    },
    onError: (err) => {
      captureException(err);
    },
  });
};
